import { useCallback } from 'react';

import type { Principal } from '../../../commonTypes.tsx';

import type { SpecificAccessLiveMutationArgs } from './specificAccessDialogTypes.ts';
import {
	buildUpdateContentDirectRestrictionsBulkRemoveVariables,
	buildUpdateContentDirectRestrictionsRemoveVariables,
	buildUpdateContentDirectRestrictionsVariables,
} from './specificAccessDialogDirectRestrictionsPayload.ts';
import type {
	useUpdateContentDirectRestrictionsMutation$data as MutationData,
	useUpdateContentDirectRestrictionsMutation$variables as MutationVariables,
} from './__generated__/useUpdateContentDirectRestrictionsMutation.graphql.ts';
import type { SpecificAccessFlags } from './useSpecificAccessFlags.tsx';

export type PrincipalById = Map<string, { principal: Principal }>;

export function useSpecificAccessLiveMutation({
	cloudId,
	contentId,
	includeInvites,
	principalById,
	mutateDirectRestrictions,
	flags,
}: {
	cloudId: string;
	contentId: string;
	includeInvites: boolean;
	principalById: Map<string, { principal: Principal }>;
	mutateDirectRestrictions: (options: {
		variables: MutationVariables;
	}) => Promise<{ data?: MutationData } | undefined>;
	flags: SpecificAccessFlags;
}): {
	triggerLiveMutation: (args: SpecificAccessLiveMutationArgs) => Promise<void>;
} {
	const triggerLiveMutation = useCallback(
		async (args: SpecificAccessLiveMutationArgs) => {
			try {
				if (args.type === 'remove-all') {
					const principalsToRemove: Principal[] = [];
					for (const id of args.principalIdsToRemove) {
						const principalEntry = principalById.get(id);
						if (principalEntry) {
							principalsToRemove.push(principalEntry.principal);
						}
					}

					if (principalsToRemove.length === 0) {
						return;
					}

					const bulkRemoveVariables = buildUpdateContentDirectRestrictionsBulkRemoveVariables({
						cloudId,
						contentId,
						includeInvites,
						principals: principalsToRemove,
					});

					const bulkRemoveRes = await mutateDirectRestrictions({ variables: bulkRemoveVariables });
					const bulkRemovePayload = bulkRemoveRes?.data?.confluence_updateContentDirectRestrictions;
					if (
						!bulkRemovePayload?.success ||
						(bulkRemovePayload.errors && bulkRemovePayload.errors.length > 0)
					) {
						// TODO: add analytics
						flags.showAccessChangeFailedFlag();
						return;
					}

					flags.showAccessUpdatedFlag();
					return;
				}

				const entry = principalById.get(args.principalId);
				if (!entry) {
					return;
				}

				const variables =
					args.type === 'remove'
						? buildUpdateContentDirectRestrictionsRemoveVariables({
								cloudId,
								contentId,
								includeInvites,
								principal: entry.principal,
							})
						: buildUpdateContentDirectRestrictionsVariables({
								cloudId,
								contentId,
								includeInvites,
								principal: entry.principal,
								previousRole: args.previousRole,
								nextRole: args.nextRole,
							});

				const res = await mutateDirectRestrictions({ variables });
				const payload = res?.data?.confluence_updateContentDirectRestrictions;
				if (!payload?.success || (payload.errors && payload.errors.length > 0)) {
					// TODO: add analytics
					flags.showAccessChangeFailedFlag();
					return;
				}

				flags.showAccessUpdatedFlag();
			} catch (e) {
				void e;
				flags.showAccessChangeFailedFlag();
			}
		},
		[cloudId, contentId, flags, includeInvites, mutateDirectRestrictions, principalById],
	);

	return { triggerLiveMutation };
}
